import React, { useState, useContext } from "react";
import styled, { keyframes } from "styled-components";
import axios from "axios";
import { GlobalState } from "../../GlobalState";
import { editAccess } from "../utils/utils";
import { FiLock, FiX, FiShield,FiEye, FiEyeOff } from "react-icons/fi";

const fadeIn = keyframes`
  from { 
    opacity: 0;
    transform: scale(0.95);
  }
  to { 
    opacity: 1;
    transform: scale(1);
  }
`;

const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  // background: rgba(102, 126, 234, 0.4);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${fadeIn} 0.3s ease-out;
  z-index: 999;
  padding: 1rem;
`;

const ModalContent = styled.div`
  background: linear-gradient(135deg, #f5f7ff 0%, #ede9f5 100%);
  border-radius: 20px;
  width: 100%;
  max-width: 420px;
  padding: 2.5rem 2rem;
  box-shadow: 0 20px 60px rgba(102, 126, 234, 0.3);
  animation: ${slideUp} 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  position: relative;
  border: 2px solid rgba(118, 75, 162, 0.2);
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  margin-bottom: 2rem;
`;

const IconWrapper = styled.div`
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #107066 0%, #0b5a52 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
`;

const Title = styled.h2`
  margin: 0;
  font-size: 1.5rem;
  font-weight: 700;
  background: linear-gradient(135deg, #107066 0%, #0b5a52 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  width: 36px;
  height: 36px;
  background: rgba(102, 126, 234, 0.1);
  border: none;
  border-radius: 8px;
  cursor: pointer;
  color: #107066;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(16, 112, 102, 0.2);
    transform: rotate(90deg);
  }
`;

const ShowFormToggle = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  cursor: pointer;
  margin: 1rem auto;
  color: white;
  background: linear-gradient(135deg, #107066 0%, #0b5a52 100%);
  border: none;
  outline: none;
  font-size: 1rem;
  font-weight: 600;
  padding: 0.875rem 2rem;
  border-radius: 12px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(102, 126, 234, 0.5);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const InputGroup = styled.div`
  margin-bottom: 1.25rem;
  position: relative;
`;

const InputLabel = styled.label`
  display: block;
  font-size: 0.875rem;
  color: #5a4a8a;
  margin-bottom: 0.5rem;
  font-weight: 600;
`;

const InputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const InputIcon = styled.div`
  position: absolute;
  left: 1rem;
  color: #764ba2;
  display: flex;
  align-items: center;
  pointer-events: none;
`;

const StyledInput = styled.input`
  background: white;
  padding: 0.875rem 1rem 0.875rem 3rem;
  font-size: 1rem;
  border-radius: 12px;
  width: 100%;
  border: 2px solid #d4c5f9;
  color: #5a4a8a;
  transition: all 0.3s ease;
  
  &:focus {
    border-color: #107066;
    outline: none;
    box-shadow: 0 0 0 3px rgba(16, 112, 102, 0.1);
  }
  
  &::placeholder {
    color: #b8a9dc;
  }
`;

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 1rem;
`;

const SubmitButton = styled.button`
  flex: 1;
  cursor: pointer;
  color: white;
  background: linear-gradient(135deg, #107066 0%, #0b5a52 100%);
  border: none;
  outline: none;
  font-size: 1rem;
  font-weight: 600;
  padding: 0.875rem 1.5rem;
  border-radius: 12px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(16, 112, 102, 0.4);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(16, 112, 102, 0.5);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const CancelButton = styled.button`
  flex: 1;
  cursor: pointer;
  background: rgba(16, 112, 102, 0.1);
  color: #107066;
  border: 2px solid #d4c5f9;
  outline: none;
  font-size: 1rem;
  font-weight: 600;
  padding: 0.875rem 1.5rem;
  border-radius: 12px;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(16, 112, 102, 0.2);
    border-color: #b8a9dc;
  }
  
  &:active {
    transform: scale(0.98);
  }
`;

const ErrMessage = styled.p`
  font-size: 0.875rem;
  color: #dc2626;
  text-align: center;
  margin-top: 1rem;
  padding: 0.75rem;
  background: #fef2f2;
  border-radius: 8px;
  border-left: 3px solid #dc2626;
`;

const PasswordStrengthIndicator = styled.div`
  margin-top: 0.5rem;
  font-size: 0.75rem;
  color: #764ba2;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const EyeIcon = styled.div`
  position: absolute;
  right: 12px;
  cursor: pointer;
  color: #666;
  display: flex;
  align-items: center;
`;


export default function UpdatePassword({ loading, userData }) {
  const initialValues = {
    old_password: "",
    new_password: "",
    retype_new_password: "",
  };
  const state = useContext(GlobalState);

  const [showForm, setShowForm] = useState(false);
  const [values, setValues] = useState(initialValues);
  const [err, setErr] = useState("");
  const [showPassword, setShowPassword] = useState({
  old: false,
  new: false,
  confirm: false,
});



  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setValues({
      ...values,
      [name]: value,
    });
  };

  const reset = () => setValues(initialValues);

  const handleClose = () => {
    setShowForm(false);
    setErr("");
    reset();
  };

  const UpdatePasswordSubmit = async (e) => {
    e.preventDefault();
    let hasErr = 0;

    if (values.new_password !== values.retype_new_password) {
      setErr("Passwords do not match.");
      hasErr = 1;
    }

    if (values.new_password.length < 8) {
      setErr("Password must be at least 8 characters long.");
      hasErr = 1;
    }

    if (!hasErr) {
      try {
        const res = await axios.put(
          `${process.env.REACT_APP_API}/user/update_password`,
          {
            old_password: values.old_password,
            new_password: values.new_password,
          },
          {
            headers: { Authorization: state.token[0] },
          }
        );

        if (res.status === 200) {
          handleClose();
          // Server sends logout:true after a password change to force re-authentication.
          // Clear the access token and redirect so the user logs in fresh with the new password.
          if (res.data?.logout) {
            localStorage.removeItem("accessToken");
            localStorage.removeItem("org_id");
            sessionStorage.setItem("autoLoggedOut", "true");
            window.location.reload();
          }
        }
      } catch (err) {
        setErr(err.response?.data?.error || "Something went wrong");
      }
    }
  };



  // if (!editAccess(userData)) return null;

  return (
    <>
      <ShowFormToggle onClick={() => setShowForm(true)}>
        <FiLock size={18} />
        Change Password
      </ShowFormToggle>

      {showForm && (
        <ModalOverlay onClick={handleClose}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <CloseButton onClick={handleClose}>
              <FiX size={20} />
            </CloseButton>
            
            <Header>
              <IconWrapper>
                <FiShield size={24} />
              </IconWrapper>
              <Title>Change Password</Title>
            </Header>
            
            <StyledForm onSubmit={UpdatePasswordSubmit}>
              <InputGroup>
                <InputLabel>Current Password</InputLabel>
                <InputWrapper>
                  <InputIcon>
                    <FiLock size={18} />
                  </InputIcon>
                    <StyledInput
                    type={showPassword.old ? "text" : "password"}
                    name="old_password"
                    placeholder="Enter current password"
                    value={values.old_password}
                    onChange={handleInputChange}
                    required
                  />
                  <EyeIcon onClick={() => setShowPassword(prev => ({ ...prev, old: !prev.old }))}>
                    {showPassword.old ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                  </EyeIcon>

                </InputWrapper>
              </InputGroup>

              <InputGroup>
                <InputLabel>New Password</InputLabel>
                <InputWrapper>
                  <InputIcon>
                    <FiLock size={18} />
                  </InputIcon>
                    <StyledInput
                      type={showPassword.new ? "text" : "password"}
                      name="new_password"
                      placeholder="Enter new password"
                      value={values.new_password}
                      onChange={handleInputChange}
                      required
                    />
                    <EyeIcon onClick={() => setShowPassword(prev => ({ ...prev, new: !prev.new }))}>
                      {showPassword.new ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                    </EyeIcon>
                </InputWrapper>
                <PasswordStrengthIndicator>
                  <FiShield size={14} />
                  Minimum 8 characters required
                </PasswordStrengthIndicator>
              </InputGroup>

              <InputGroup>
                <InputLabel>Confirm New Password</InputLabel>
                <InputWrapper>
                  <InputIcon>
                    <FiLock size={18} />
                  </InputIcon>
                    <StyledInput
                      type={showPassword.confirm ? "text" : "password"}
                      name="retype_new_password"
                      placeholder="Confirm new password"
                      value={values.retype_new_password}
                      onChange={handleInputChange}
                      required
                    />
                    <EyeIcon onClick={() => setShowPassword(prev => ({ ...prev, confirm: !prev.confirm }))}>
                      {showPassword.confirm ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                    </EyeIcon>
                </InputWrapper>
              </InputGroup>

              {err && <ErrMessage>{err}</ErrMessage>}

              <ButtonGroup>
                <SubmitButton type="submit">Update Password</SubmitButton>
                <CancelButton type="button" onClick={handleClose}>
                  Cancel
                </CancelButton>
              </ButtonGroup>
            </StyledForm>
          </ModalContent>
        </ModalOverlay>
      )}
    </>
  );
}
